import { action, observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"

import type { MouseEvent as ReactMouseEvent } from "react"


export class ResizableSplitterModel extends Model {
  private readonly onResize: (leftPercentage: number, rightPercentage: number) => void

  private readonly minLeftPercentage: number
  private readonly maxLeftPercentage: number

  private readonly minLeftPixels: number
  private readonly minRightPixels: number

  private readonly defaultLeftPercentage = 40
  private readonly defaultRightPercentage = 60

  readonly isDragging = observable.box(false)
  readonly leftPercentage = observable.box(this.defaultLeftPercentage)
  readonly rightPercentage = observable.box(this.defaultRightPercentage)

  private startX = 0
  private startLeftPercentage = 0
  private containerWidth = 0

  constructor(options: {
    onResize: (leftPercentage: number, rightPercentage: number) => void
    initialLeftPercentage: number
    minLeftPercentage: number
    maxLeftPercentage: number
    minLeftPixels: number
    minRightPixels: number
  }) {
    super("ResizableSplitterModel")

    this.onResize = options.onResize
    this.minLeftPercentage = options.minLeftPercentage
    this.maxLeftPercentage = options.maxLeftPercentage
    this.minLeftPixels = options.minLeftPixels
    this.minRightPixels = options.minRightPixels

    runInAction(() => {
      if (options.initialLeftPercentage !== undefined) {
        this.leftPercentage.set(options.initialLeftPercentage)
        this.rightPercentage.set(100 - options.initialLeftPercentage)
      }
    })
  }

  readonly startDrag = action((event: ReactMouseEvent, containerElement: HTMLElement) => {
    event.preventDefault()

    this.isDragging.set(true)
    this.startX = event.clientX
    this.startLeftPercentage = this.leftPercentage.get()
    this.containerWidth = containerElement.getBoundingClientRect().width

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - this.startX
      const deltaPercentage = (deltaX / this.containerWidth) * 100
      let newLeftPercentage = Math.max(
        this.minLeftPercentage,
        Math.min(this.maxLeftPercentage, this.startLeftPercentage + deltaPercentage)
      )

      // Apply pixel-based constraints
      if (this.minLeftPixels !== undefined) {
        const minLeftPercentageFromPixels = (this.minLeftPixels / this.containerWidth) * 100
        newLeftPercentage = Math.max(newLeftPercentage, minLeftPercentageFromPixels)
      }

      if (this.minRightPixels !== undefined) {
        const maxLeftPercentageFromPixels = ((this.containerWidth - this.minRightPixels) / this.containerWidth) * 100
        newLeftPercentage = Math.min(newLeftPercentage, maxLeftPercentageFromPixels)
      }

      const newRightPercentage = 100 - newLeftPercentage

      this.leftPercentage.set(newLeftPercentage)
      this.rightPercentage.set(newRightPercentage)
      this.onResize(newLeftPercentage, newRightPercentage)
    }

    const handleMouseUp = () => {
      runInAction(() => this.isDragging.set(false))
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.classList.remove("resize-in-progress")
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.body.classList.add("resize-in-progress")
  })

  readonly resetToDefault = () => {
    runInAction(() => {
      this.leftPercentage.set(this.defaultLeftPercentage)
      this.rightPercentage.set(this.defaultRightPercentage)
      this.onResize(this.defaultLeftPercentage, this.defaultRightPercentage)
    })
  }
}
